/**
 * MED-781: Needs Attention rail item types.
 *
 * Each variant carries the minimum data its card needs to render — the rail
 * stays decoupled from the API shape so future card types can be added
 * without changing the rail's container.
 */

import type { CalendlyBookingHealth, MissClaim, MissingMeetingLink } from '../../../../types';

export type NeedsAttentionType =
  | 'starts_today'
  | 'overdue_attendance'
  | 'post_session_feedback'
  | 'prep_needed'
  | 'session_report_ready'
  | 'connect_calendly'
  | 'calendly_booking_blocked'
  | 'meeting_link_missing'
  | 'calendly_20min_unmapped'
  | 'miss_claim'
  | 'feedback';

interface NeedsAttentionBase {
  id: string;
  type: NeedsAttentionType;
}

/**
 * MED-1147: participant display-name divergence fields shared by every
 * session-derived card. `participantDisplayDiffers` (server-computed, AC1) gates
 * the internal-awareness indicator; `participantDisplayName` is the value its
 * tooltip carries ("Participants see: {name}"). Mixed into the five
 * session-label item variants — the non-session cards (Connect Calendly, survey
 * Feedback) deliberately omit them.
 */
export interface SessionLabelDivergenceFields {
  participantDisplayName: string | null;
  participantDisplayDiffers: boolean;
}

export interface StartsTodayItem extends NeedsAttentionBase, SessionLabelDivergenceFields {
  type: 'starts_today';
  cohortId: string;
  sessionId: string;
  cohortName: string;
  sessionTitle: string;
  scheduledStart: string;
  participantCount: number;
  groupName: string;
  /** MED-1002: 'in_progress' drives "Rejoin Session" label/route. */
  status: 'scheduled' | 'in_progress';
}

export interface OverdueAttendanceItem extends NeedsAttentionBase, SessionLabelDivergenceFields {
  type: 'overdue_attendance';
  cohortId: string;
  sessionId: string;
  cohortName: string;
  sessionTitle: string;
  groupName: string;
  scheduledEnd: string;
  daysOverdue: number;
}

/**
 * MED-867: A completed MED02 session whose Medley Feedback Form is unsubmitted.
 * Sibling of OverdueAttendanceItem — same red "Overdue" treatment, differentiated
 * lexically (feedback-naming title + "Submit Feedback" CTA). `daysPending` is the
 * elapsed-time indicator ("Pending N days"), mirroring attendance's daysOverdue.
 */
export interface PostSessionFeedbackItem extends NeedsAttentionBase, SessionLabelDivergenceFields {
  type: 'post_session_feedback';
  cohortId: string;
  sessionId: string;
  cohortName: string;
  sessionTitle: string;
  groupName: string;
  scheduledEnd: string;
  daysPending: number;
}

export interface PrepNeededItem extends NeedsAttentionBase, SessionLabelDivergenceFields {
  type: 'prep_needed';
  cohortId: string;
  sessionId: string;
  cohortName: string;
  sessionTitle: string;
  scheduledStart: string;
  participantCount: number;
  groupName: string;
  /**
   * MED-781 Comment 3: when the session is today and >60 minutes away,
   * the Prep Needed card renders with orange urgency styling. Within 60
   * minutes the card is replaced by Starts Today (controlled by the hook,
   * not this flag).
   */
  isSameDayUrgent: boolean;
}

export interface SessionReportReadyItem extends NeedsAttentionBase, SessionLabelDivergenceFields {
  type: 'session_report_ready';
  sessionId: string;
  cohortId: string;
  cohortName: string;
  // MED-1106: `sessionTitle` is the composed label ("Session N: {topic}") — the
  // resolved topic lives here now, so there is no separate topicName field.
  sessionTitle: string;
  scheduledEnd: string;
  attendanceMarkedAt: string | null;
}

export interface ConnectCalendlyItem extends NeedsAttentionBase {
  type: 'connect_calendly';
}

/**
 * MED-1363: a required question stops Calendly from taking bookings. The
 * coach can fix it. MED-1365: participants still book online, through a
 * Google Calendar invite from Medley. A free or trial plan is no longer an
 * item, because nothing stops the bookings.
 */
export interface CalendlyBookingBlockedItem extends NeedsAttentionBase {
  type: 'calendly_booking_blocked';
  problem: 'required_question';
  /** The event type name or names, for a required question. */
  eventTypeName: string | null;
}

/**
 * MED-1365 QA round 6: an active cohort of the coach uses a meeting platform
 * and the coach's profile has no link for it. Saving the link removes it.
 */
export interface MeetingLinkMissingItem extends NeedsAttentionBase {
  type: 'meeting_link_missing';
  link: MissingMeetingLink;
}

/**
 * MED-1365 QA round 9: Calendly is connected and the 20 min length has no
 * usable event type (none mapped, another length, or off). Mapping one
 * removes it.
 */
export interface Calendly20minUnmappedItem extends NeedsAttentionBase {
  type: 'calendly_20min_unmapped';
  planStage: CalendlyBookingHealth['plan_stage'];
  /** The coach's 20 min event types that are off, for the prompt. */
  offTwentyNames: string[];
}

/**
 * MED-1346: a late cancel or reschedule of a 1:1 that waits for the coach to
 * say whether the notice was under 24 hours. Answering removes it.
 */
export interface MissClaimItem extends NeedsAttentionBase {
  type: 'miss_claim';
  claim: MissClaim;
}

export interface FeedbackItem extends NeedsAttentionBase {
  type: 'feedback';
  cohortId: string;
  cohortName: string;
  surveyType: 'midpoint' | 'endpoint';
}

export type NeedsAttentionItem =
  | StartsTodayItem
  | OverdueAttendanceItem
  | PostSessionFeedbackItem
  | PrepNeededItem
  | SessionReportReadyItem
  | ConnectCalendlyItem
  | CalendlyBookingBlockedItem
  | MeetingLinkMissingItem
  | Calendly20minUnmappedItem
  | MissClaimItem
  | FeedbackItem;

/**
 * Sort priority — lower wins. Drives AC8 (MED-781) and MED-867 AC10.
 *   1. Starts Today
 *   2. Overdue Attendance
 *   3. Submit Session Feedback (MED-867 AC10 — immediately after Overdue
 *      Attendance so a session's two close-out tasks cluster together)
 *   4. Prep Needed
 *   5. Session Report Ready
 *   6. Connect Calendly, and Calendly booking blocked (MED-1363). Both
 *      cannot show at once: the second needs a connection. Also a missing
 *      meeting link (MED-1365), and an unmapped 20 min length (MED-1365 QA
 *      round 9), beside them.
 *   (MED-1346: a late change to confirm sits with Starts Today, at 1, so the
 *   coach sees it before the attendance cards.)
 *   7. Midpoint Feedback
 *   8. Endpoint Feedback
 */
export const NEEDS_ATTENTION_TYPE_PRIORITY: Record<NeedsAttentionType, number> = {
  starts_today: 1,
  overdue_attendance: 2,
  miss_claim: 1,
  post_session_feedback: 3,
  prep_needed: 4,
  session_report_ready: 5,
  connect_calendly: 6,
  calendly_booking_blocked: 6,
  meeting_link_missing: 6,
  calendly_20min_unmapped: 6,
  feedback: 7,
};
